import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon, Logo } from '../design/Icon';
import { initials } from '../design/format';
import { Avatar } from '../components/shared/People';
import { Popover, usePopover, MenuItem, MenuTitle } from '../components/shared/Overlay';
import { navLabel, roleLabel, visibleNavSections } from './navConfig';
import { useAuth } from '../contexts/AuthContext';
import { orgFullName, useShellStatus, useSyncLine } from './ShellStatus';

function OrgSwitcher({ orgName }) {
  const pop = usePopover();
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      width={220}
      block
      label="Organization"
      anchor={
        <button className="nav-org" type="button" aria-label={`Organization: ${orgName}`} aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle}>
          <span className="org-mark">{initials(orgName)}</span>
          <span className="org-name" title={orgName}>
            {orgName}
          </span>
          <Icon name="chevD" />
        </button>
      }
    >
      <MenuTitle>Organization</MenuTitle>
      <MenuItem on checkable icon="building" onClick={pop.close}>
        {orgName}
      </MenuItem>
    </Popover>
  );
}

function SyncStatus() {
  const { text, healthy } = useSyncLine();
  return (
    <div className="sync-status">
      <span className="sync-dot" aria-hidden="true" style={healthy ? undefined : { background: 'var(--nav-muted)', boxShadow: 'none' }} />
      <span>{text}</span>
    </div>
  );
}

const SCROLLING_CLASS_MS = 900;

function useScrolling() {
  const [scrolling, setScrolling] = useState(false);
  const timer = useRef(null);
  const onScroll = useCallback(() => {
    setScrolling(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setScrolling(false), SCROLLING_CLASS_MS);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [scrolling, onScroll];
}

export function Nav({ user, onSignOut }) {
  const { status, terms } = useShellStatus();
  const [scrolling, onScroll] = useScrolling();
  const { hasPermission } = useAuth();
  const orgName = orgFullName(status);
  const counts = status?.attention || {};
  return (
    <nav className={scrolling ? 'nav is-scrolling' : 'nav'} id="vl-nav" aria-label="Main" onScroll={onScroll}>
      <div className="nav-brand">
        <Logo size={28} />
        <span>Vertolytics</span>
      </div>
      <OrgSwitcher orgName={orgName} />
      {visibleNavSections(hasPermission).map((section) => (
        <div className="nav-section" key={section.title}>
          <div className="nav-section-title">{section.title}</div>
          {section.items.map((item) => {
            const count = item.countKey ? counts[item.countKey] : null;
            return (
              <NavLink key={item.id} to={item.to}>
                <Icon name={item.icon} />
                <span>{navLabel(item, terms)}</span>
                {count ? (
                  <span className="count alert" title="Immediate and Within 48 Hours items in the last 7 days">
                    {count}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </div>
      ))}
      <div className="nav-foot">
        <SyncStatus />
        <div className="me">
          <Avatar name={user?.name || ''} />
          <div className="grow">
            <div className="who">{user?.name}</div>
            <div className="role">{roleLabel(user?.roles)}</div>
          </div>
          <a
            href="/login"
            title="Sign out"
            aria-label="Sign out"
            style={{ padding: 6 }}
            onClick={(e) => {
              e.preventDefault();
              onSignOut();
            }}
          >
            <Icon name="logout" />
          </a>
        </div>
      </div>
    </nav>
  );
}
